import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Sparkles, FileText, Pencil, Loader2, Inbox, ArrowLeft, Copy, Video, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EXAM_LABELS, SUBJECT_LABELS, DIFFICULTY_LABELS, difficultyClass } from "@/lib/pyqMeta";
import { QuestionContent } from "@/components/QuestionContent";
import { MathText } from "@/components/MathText";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const STATUS_TABS = [
  { key: "PENDING_REVIEW", label: "Pending" },
  { key: "PUBLISHED", label: "Published" },
  { key: "REJECTED", label: "Rejected" },
];

const EditDialog = ({ q, open, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [options, setOptions] = useState([]);
  const [correct, setCorrect] = useState("");
  const [difficulty, setDifficulty] = useState("MEDIUM");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (q) {
      setText(q.questionText || "");
      setOptions(q.options || []);
      setCorrect(Array.isArray(q.correctAnswer) ? q.correctAnswer.join(",") : q.correctAnswer || "");
      setDifficulty(q.difficulty || "MEDIUM");
    }
  }, [q]);

  const save = async () => {
    setSaving(true);
    try {
      const correctAnswer = correct.includes(",") ? correct.split(",").map((s) => s.trim()) : correct.trim();
      const { data } = await api.put(`/review/${q.id}`, { questionText: text, options, correctAnswer, difficulty });
      toast.success("Question updated");
      onSaved(data);
      onClose();
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!q) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit & correct question</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Question text (LaTeX with $…$)</Label>
            <Textarea data-testid="edit-question-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} className="rounded-xl font-mono text-sm" />
          </div>
          <div className="space-y-2">
            <Label>Options</Label>
            {options.map((o, i) => (
              <div key={o.id} className="flex items-center gap-2">
                <span className="w-6 text-sm font-bold uppercase text-muted-foreground">{o.id}</span>
                <Input
                  value={o.text}
                  data-testid={`edit-option-${o.id}`}
                  onChange={(e) => setOptions((prev) => prev.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                  className="rounded-xl"
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Correct answer (id, or comma-separated)</Label>
              <Input data-testid="edit-correct" value={correct} onChange={(e) => setCorrect(e.target.value)} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["EASY", "MEDIUM", "HARD"].map((d) => <SelectItem key={d} value={d}>{DIFFICULTY_LABELS[d]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReviewQueue = ({ jobId = null, onClearJob, onOpenFeedback }) => {
  const [status, setStatus] = useState("PENDING_REVIEW");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [aiOnly, setAiOnly] = useState(false);
  const [comparing, setComparing] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [videoMode, setVideoMode] = useState(false);
  const [videoItems, setVideoItems] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  useEffect(() => {
    api.get("/admin/feedback/summary").then((r) => setFeedbackMap(r.data.byQuestion || {})).catch(() => {});
  }, []);
  const token = (typeof window !== "undefined" && window.localStorage.getItem("jm_token")) || "";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const loadVideos = () => {
    setLoading(true);
    api.get("/review/videos")
      .then((r) => setVideoItems(r.data.items))
      .finally(() => setLoading(false));
  };

  const toggleEligible = async (q) => {
    const next = !q.videoEligible;
    try {
      await api.put(`/admin/questions/${q.id}/video-eligible`, { eligible: next });
      setItems((prev) => prev.map((x) => (x.id === q.id ? { ...x, videoEligible: next } : x)));
      toast.success(next ? "Video solution enabled for this question" : "Video solution disabled");
    } catch {
      toast.error("Could not update");
    }
  };

  const videoAction = async (qid, kind) => {
    setBusy(qid);
    try {
      await api.post(`/review/${qid}/video/${kind}`);
      toast.success(kind === "approve" ? "Video published to students" : "Video rejected");
      setVideoItems((prev) => prev.filter((q) => q.id !== qid));
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(null);
    }
  };

  const load = () => {
    if (videoMode) return loadVideos();
    setLoading(true);
    const params = jobId ? { jobId } : { status };
    if (aiOnly) params.solutionSource = "AI_GENERATED";
    api.get("/review/queue", { params })
      .then((r) => { setItems(r.data.items); setCounts(r.data.counts); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [status, jobId, aiOnly, videoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === "approve") await api.post(`/review/${id}/approve`);
      else await api.post(`/review/${id}/reject`, { reason: "Rejected by reviewer" });
      toast.success(kind === "approve" ? "Published to students" : "Rejected");
      setItems((prev) => prev.filter((q) => q.id !== id));
      setCounts((c) => ({ ...c, [status]: Math.max(0, (c[status] || 1) - 1) }));
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(null);
    }
  };

  const pendingInView = items.filter((q) => q.status === "PENDING_REVIEW");
  const dupPending = pendingInView.filter((q) => q.isDuplicate);
  const aiPending = pendingInView.filter((q) => q.solutionSource === "AI_GENERATED");

  const approveAll = async () => {
    if (!jobId || pendingInView.length === 0) return;
    setBusy("batch");
    try {
      const { data } = await api.post("/review/approve-batch", { jobId });
      toast.success(`Approved ${data.approved} question${data.approved === 1 ? "" : "s"} from this batch`);
      load();
    } catch {
      toast.error("Could not approve the batch");
    } finally {
      setBusy(null);
    }
  };

  const rejectDuplicates = async () => {
    if (!jobId || dupPending.length === 0) return;
    setBusy("dupes");
    try {
      const { data } = await api.post("/review/reject-duplicates", { jobId });
      toast.success(`Rejected ${data.rejected} duplicate${data.rejected === 1 ? "" : "s"}`);
      load();
    } catch {
      toast.error("Could not reject duplicates");
    } finally {
      setBusy(null);
    }
  };

  const approveAllPending = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/review/approve-all-pending");
      toast.success(`Published ${data.approved} pending question${data.approved === 1 ? "" : "s"}`);
      setConfirmAll(false);
      setStatus("PENDING_REVIEW");
      load();
    } catch {
      toast.error("Could not approve pending questions");
    } finally {
      setBusy(null);
    }
  };

  const openOriginal = async (q) => {
    if (!q.duplicateOf) return;
    setComparing({ current: q, original: null });
    try {
      const { data } = await api.get(`/pyq/${q.duplicateOf}`);
      setComparing({ current: q, original: data });
    } catch {
      toast.error("Could not load the original question");
      setComparing(null);
    }
  };

  return (
    <div>
      {jobId ? (
        <div className="mb-5 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="review-batch-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-medium">Showing questions from the selected generation batch</span>
            <div className="flex flex-wrap items-center gap-2">
              {dupPending.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={rejectDuplicates}
                  disabled={busy === "dupes"}
                  data-testid="review-reject-duplicates"
                  className="gap-1.5 rounded-full border-red-500/40 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  {busy === "dupes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                  Reject all duplicates ({dupPending.length})
                </Button>
              )}
              {pendingInView.length > 0 && (
                <Button
                  size="sm"
                  onClick={approveAll}
                  disabled={busy === "batch"}
                  data-testid="review-approve-all"
                  className="gap-1.5 rounded-full"
                >
                  {busy === "batch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Approve all ({pendingInView.length})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClearJob} data-testid="review-clear-batch" className="gap-1.5">
                <ArrowLeft className="h-4 w-4" /> All questions
              </Button>
            </div>
          </div>
          {!loading && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="review-batch-summary">
              <span><b className="text-foreground">{pendingInView.length}</b> pending</span>
              <span className="opacity-40">·</span>
              <span><b className="text-red-600 dark:text-red-400">{dupPending.length}</b> flagged duplicate</span>
              <span className="opacity-40">·</span>
              <span><b className="text-amber-600 dark:text-amber-400">{aiPending.length}</b> AI-solution</span>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`review-tab-${t.key}`}
              onClick={() => setStatus(t.key)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                status === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-muted-foreground"
              )}
            >
              {t.label} {counts[t.key] != null && <span className="ml-1 opacity-70">({counts[t.key]})</span>}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {status === "PENDING_REVIEW" && (counts.PENDING_REVIEW || 0) > 0 && (
              <Button
                size="sm"
                onClick={() => setConfirmAll(true)}
                disabled={busy === "all"}
                data-testid="review-approve-all-pending"
                className="gap-1.5 rounded-full"
              >
                {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Approve all pending ({counts.PENDING_REVIEW})
              </Button>
            )}
            <button
              data-testid="review-filter-ai-only"
              onClick={() => setAiOnly((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                aiOnly ? "border-amber-500 bg-amber-500 text-white" : "border-amber-500/40 bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
              )}
              title="Show only questions whose solution was written by AI (handwritten-origin) — verify these first"
            >
              <Sparkles className="h-3.5 w-3.5" /> AI solutions only
            </button>
            <button
              data-testid="review-video-mode"
              onClick={() => setVideoMode((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                videoMode ? "border-primary bg-primary text-primary-foreground" : "border-primary/40 bg-primary/5 text-primary"
              )}
              title="Review student-requested video solutions awaiting approval"
            >
              <Video className="h-3.5 w-3.5" /> Video reviews
            </button>
          </div>
        </div>
      )}

      {videoMode ? (
        loading ? (
          <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-72 w-full rounded-2xl" />)}</div>
        ) : videoItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="video-review-empty">
            <Video className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-heading text-lg font-semibold">No videos awaiting review</p>
            <p className="mt-1 text-sm text-muted-foreground">When a student requests a video solution, it appears here for your approval before others can see it.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="video-review-list">
            {videoItems.map((q) => (
              <div key={q.id} data-testid={`video-review-card-${q.id}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge className="rounded-full"><Video className="mr-1 h-3 w-3" /> Video pending</Badge>
                  <span className="text-xs font-semibold uppercase text-muted-foreground">{EXAM_LABELS[q.examType] || q.examType} · {SUBJECT_LABELS[q.subject] || q.subject}</span>
                  <span className="text-xs text-muted-foreground">{q.chapter}</span>
                </div>
                <QuestionContent question={q} className="mb-3 text-[15px] leading-relaxed" />
                <video
                  data-testid={`video-review-player-${q.id}`}
                  src={`${BACKEND}/api/pyq/${q.id}/video/file?auth=${encodeURIComponent(token)}`}
                  controls
                  className="w-full rounded-lg bg-black"
                />
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => videoAction(q.id, "approve")} disabled={busy === q.id} data-testid={`video-approve-${q.id}`} className="rounded-full">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve & publish
                  </Button>
                  <Button variant="outline" onClick={() => videoAction(q.id, "reject")} disabled={busy === q.id} data-testid={`video-reject-${q.id}`} className="rounded-full">
                    <XCircle className="mr-1.5 h-4 w-4" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : loading ? (
        <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-56 w-full rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="review-empty">
          <Inbox className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-heading text-lg font-semibold">Nothing here</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {jobId ? "This batch has no questions." : `No questions in “${STATUS_TABS.find((t) => t.key === status).label}”.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((q) => {
            const correctSet = Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer];
            return (
              <div key={q.id} data-testid={`review-card-${q.id}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge variant={q.source === "AI_GENERATED" ? "default" : "outline"} className="rounded-full">
                    {q.source === "AI_GENERATED" ? <><Sparkles className="mr-1 h-3 w-3" /> AI</> : <><FileText className="mr-1 h-3 w-3" /> PYQ</>}
                  </Badge>
                  <span className="text-xs font-semibold uppercase text-muted-foreground">{EXAM_LABELS[q.examType] || q.examType} · {SUBJECT_LABELS[q.subject] || q.subject}</span>
                  <Badge className={cn("rounded-full border", difficultyClass(q.difficulty))}>{DIFFICULTY_LABELS[q.difficulty]}</Badge>
                  <span className="text-xs text-muted-foreground">{q.chapter}</span>
                  <Badge variant="outline" className="rounded-full text-[10px]">{q.status.replace("_", " ")}</Badge>
                  {q.isDuplicate && (
                    <button
                      type="button"
                      onClick={() => openOriginal(q)}
                      data-testid={`duplicate-flag-${q.id}`}
                      className="inline-flex items-center rounded-full border border-red-500 px-2 py-0.5 text-[10px] font-semibold text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                      title="View the existing question this duplicates"
                    >
                      <Copy className="mr-1 h-3 w-3" /> Possible duplicate · view original
                    </button>
                  )}
                  {q.solutionSource && (
                    <Badge
                      variant="outline"
                      data-testid={`solution-source-${q.id}`}
                      className={cn(
                        "rounded-full text-[10px]",
                        q.solutionSource === "PDF"
                          ? "border-green-500 text-green-600 dark:text-green-400"
                          : "border-amber-500 text-amber-600 dark:text-amber-400"
                      )}
                    >
                      {q.solutionSource === "PDF" ? "Solution: from PDF" : "Solution: AI-generated"}
                    </Badge>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleEligible(q)}
                    data-testid={`video-eligible-${q.id}`}
                    className={cn(
                      "ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                      q.videoEligible
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-secondary text-muted-foreground hover:text-foreground"
                    )}
                    title="Allow students to request a video solution for this question"
                  >
                    <Video className="h-3.5 w-3.5" />
                    {q.videoEligible ? "Video: on" : "Allow video"}
                  </button>
                  {feedbackMap[q.id] > 0 && (
                    <button
                      type="button"
                      onClick={() => onOpenFeedback && onOpenFeedback(q.id)}
                      data-testid={`feedback-badge-${q.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-100 dark:bg-rose-950/30 dark:text-rose-400"
                      title="View student feedback for this question"
                    >
                      <MessageSquare className="h-3.5 w-3.5" /> {feedbackMap[q.id]} feedback
                    </button>
                  )}
                </div>

                <QuestionContent question={q} className="text-[15px] leading-relaxed" />

                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {q.options.map((o) => (
                    <div key={o.id} className={cn("flex items-center gap-2 rounded-lg border p-2.5 text-sm", correctSet.includes(o.id) ? "border-green-500 bg-green-50 dark:bg-green-950/30" : "border-border")}>
                      <span className="font-bold uppercase">{o.id}</span>
                      <MathText text={o.text} />
                      {correctSet.includes(o.id) && <CheckCircle2 className="ml-auto h-4 w-4 text-green-500" />}
                    </div>
                  ))}
                </div>

                {q.solution?.steps?.length > 0 && (
                  <details className="mt-4 rounded-xl border border-border bg-secondary/30 p-4">
                    <summary className="cursor-pointer text-sm font-medium">View solution</summary>
                    <ol className="mt-3 space-y-2">
                      {q.solution.steps.map((s, i) => <li key={i} className="text-sm"><MathText text={`${i + 1}. ${s}`} /></li>)}
                    </ol>
                  </details>
                )}

                {q.status === "PENDING_REVIEW" && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button onClick={() => act(q.id, "approve")} disabled={busy === q.id} data-testid={`approve-${q.id}`} className="rounded-full">
                      <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve
                    </Button>
                    <Button variant="outline" onClick={() => act(q.id, "reject")} disabled={busy === q.id} data-testid={`reject-${q.id}`} className="rounded-full">
                      <XCircle className="mr-1.5 h-4 w-4" /> Reject
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(q)} data-testid={`edit-${q.id}`} className="rounded-full">
                      <Pencil className="mr-1.5 h-4 w-4" /> Edit
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <EditDialog
        q={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
      />

      <Dialog open={!!comparing} onOpenChange={() => setComparing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Compare with existing question</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-red-500/40 bg-red-50/40 p-4 dark:bg-red-950/20">
              <p className="mb-2 text-xs font-semibold uppercase text-red-600 dark:text-red-400">This question (new)</p>
              {comparing?.current && <QuestionContent question={comparing.current} className="text-sm leading-relaxed" />}
            </div>
            <div className="rounded-xl border border-border bg-secondary/30 p-4">
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Existing in corpus</p>
              {!comparing?.original ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : (
                <>
                  <QuestionContent question={comparing.original} className="text-sm leading-relaxed" />
                  <p className="mt-3 text-xs text-muted-foreground">
                    {EXAM_LABELS[comparing.original.examType] || comparing.original.examType} · {comparing.original.chapter} · {comparing.original.status?.replace("_", " ")}
                  </p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            {comparing?.current?.status === "PENDING_REVIEW" && (
              <Button
                variant="outline"
                data-testid="compare-reject-btn"
                className="rounded-full border-red-500/40 text-red-600 dark:text-red-400"
                onClick={() => { const cur = comparing.current; setComparing(null); act(cur.id, "reject"); }}
              >
                <XCircle className="mr-1.5 h-4 w-4" /> Reject this duplicate
              </Button>
            )}
            <Button variant="ghost" onClick={() => setComparing(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmAll} onOpenChange={(o) => !o && setConfirmAll(false)}>
        <DialogContent data-testid="approve-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Publish all pending questions?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will publish <b className="text-foreground">{counts.PENDING_REVIEW || 0}</b> pending
            question{(counts.PENDING_REVIEW || 0) === 1 ? "" : "s"} from every upload at once — they'll
            become visible to students immediately. You can still unpublish individual questions later.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAll(false)}>Cancel</Button>
            <Button data-testid="approve-all-confirm" onClick={approveAllPending} disabled={busy === "all"} className="rounded-full">
              {busy === "all" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              Yes, publish all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
